import { useEffect, useRef } from 'react'

// Cloudflare Turnstile (anti-bot CAPTCHA). Site key z env — pokud chybí, widget se
// nevykreslí a formuláře fungují bez CAPTCHy (funkce je „spící" do doplnění klíče).
const SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) || ''
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js'

/** Je Turnstile nakonfigurovaný (existuje site key)? */
export const turnstileEnabled = () => SITE_KEY !== ''

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string
      reset: (id?: string) => void
      remove: (id?: string) => void
    }
    __turnstileLoading?: Promise<void>
  }
}

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve()
  if (window.__turnstileLoading) return window.__turnstileLoading
  window.__turnstileLoading = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script')
    s.src = SCRIPT_SRC
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Turnstile script failed to load'))
    document.head.appendChild(s)
  })
  return window.__turnstileLoading
}

interface TurnstileProps {
  /** Zavolá se s tokenem po úspěšném ověření (a '' při expiraci/chybě). */
  onVerify: (token: string) => void
  theme?: 'light' | 'dark' | 'auto'
  className?: string
}

/**
 * Vykreslí Turnstile widget. Když není site key, nevykreslí nic (a vrátí prázdný token,
 * aby odeslání formuláře nebylo zablokované).
 */
export default function Turnstile({ onVerify, theme = 'auto', className = '' }: TurnstileProps) {
  const ref = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | null>(null)
  // onVerify drž v refu, ať efekt nevykresluje widget znovu při každém renderu rodiče.
  const cb = useRef(onVerify)
  cb.current = onVerify

  useEffect(() => {
    if (!turnstileEnabled()) {
      cb.current('') // CAPTCHA vypnutá → prázdný token, backend ji stejně přeskočí
      return
    }
    let cancelled = false
    loadScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: SITE_KEY,
          theme,
          callback: (token: string) => cb.current(token),
          'expired-callback': () => cb.current(''),
          'error-callback': () => cb.current(''),
        })
      })
      .catch(() => cb.current('')) // selhání načtení nesmí zablokovat formulář
    return () => {
      cancelled = true
      if (widgetId.current && window.turnstile) {
        try { window.turnstile.remove(widgetId.current) } catch { /* ignore */ }
        widgetId.current = null
      }
    }
  }, [theme])

  if (!turnstileEnabled()) return null
  return <div ref={ref} className={className} />
}
